import { useState } from 'react';
import {
  Plus,
  Search,
  Zap,
  Play,
  Pause,
  Trash2,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  SkipForward,
  ArrowLeft,
  GripVertical,
  X,
  History,
} from 'lucide-react';
import {
  useWorkflows,
  useCreateWorkflow,
  useUpdateWorkflow,
  useDeleteWorkflow,
  useToggleWorkflow,
  useWorkflowHistory,
  type Workflow,
  type WorkflowCondition,
  type WorkflowAction,
  type WorkflowTrigger,
  type TriggerType,
  type ActionType,
  type ConditionOperator,
} from '../hooks/useWorkflows';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface WorkflowsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const triggerTypeLabels: Record<TriggerType, string> = {
  record_created: 'Record Created',
  record_updated: 'Record Updated',
  stage_changed: 'Stage Changed',
  task_completed: 'Task Completed',
  deal_won: 'Deal Won',
  deal_lost: 'Deal Lost',
  renewal_approaching: 'Renewal Approaching',
  email_received: 'Email Received',
  scheduled: 'Scheduled',
};

const actionTypeLabels: Record<ActionType, string> = {
  send_email: 'Send Email',
  create_task: 'Create Task',
  update_field: 'Update Field',
  move_stage: 'Move Stage',
  add_tag: 'Add Tag',
  remove_tag: 'Remove Tag',
  create_note: 'Create Note',
  assign_owner: 'Assign Owner',
  webhook: 'Webhook',
  wait: 'Wait',
};

const conditionOperators: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

type ViewMode = 'list' | 'form' | 'history';

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// --- Workflow Form ---

interface WorkflowFormProps {
  initial?: Workflow;
  onSave: (data: Partial<Workflow>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function WorkflowForm({ initial, onSave, onCancel, isSaving }: WorkflowFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [trigger, setTrigger] = useState<WorkflowTrigger>(
    initial?.trigger ?? { type: 'record_created' },
  );
  const [conditions, setConditions] = useState<WorkflowCondition[]>(
    initial?.conditions ?? [],
  );
  const [actions, setActions] = useState<WorkflowAction[]>(
    initial?.actions ?? [],
  );

  function addCondition() {
    setConditions([...conditions, { field: '', operator: 'equals', value: '' }]);
  }

  function removeCondition(index: number) {
    setConditions(conditions.filter((_, i) => i !== index));
  }

  function updateCondition(index: number, updates: Partial<WorkflowCondition>) {
    setConditions(conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)));
  }

  function addAction() {
    const newAction: WorkflowAction = {
      id: generateId(),
      type: 'create_task',
      config: {},
      order: actions.length,
    };
    setActions([...actions, newAction]);
  }

  function removeAction(index: number) {
    setActions(
      actions
        .filter((_, i) => i !== index)
        .map((a, i) => ({ ...a, order: i })),
    );
  }

  function updateAction(index: number, updates: Partial<WorkflowAction>) {
    setActions(actions.map((a, i) => (i === index ? { ...a, ...updates } : a)));
  }

  function moveAction(from: number, direction: 'up' | 'down') {
    const to = direction === 'up' ? from - 1 : from + 1;
    if (to < 0 || to >= actions.length) return;
    const updated = [...actions];
    [updated[from], updated[to]] = [updated[to], updated[from]];
    setActions(updated.map((a, i) => ({ ...a, order: i })));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, description, trigger, conditions, actions });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {/* Basic Info */}
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Basic Information</h3>
        <div>
          <label className="label">Name</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workflow name"
            required
          />
        </div>
        <div>
          <label className="label">Description</label>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this workflow do?"
            rows={2}
          />
        </div>
      </div>

      {/* Trigger */}
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Trigger</h3>
        <div>
          <label className="label">When this happens</label>
          <select
            className="input"
            value={trigger.type}
            onChange={(e) =>
              setTrigger({ ...trigger, type: e.target.value as TriggerType })
            }
          >
            {Object.entries(triggerTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Entity Type (optional)</label>
          <select
            className="input"
            value={trigger.entityType ?? ''}
            onChange={(e) =>
              setTrigger({
                ...trigger,
                entityType: e.target.value || undefined,
              })
            }
          >
            <option value="">Any</option>
            <option value="contact">Contact</option>
            <option value="organization">Organization</option>
            <option value="deal">Deal</option>
            <option value="task">Task</option>
          </select>
        </div>
      </div>

      {/* Conditions */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            Conditions{' '}
            <span className="font-normal text-slate-400">({conditions.length})</span>
          </h3>
          <button type="button" onClick={addCondition} className="btn-secondary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Condition
          </button>
        </div>

        {conditions.length === 0 && (
          <p className="text-sm text-slate-400">
            No conditions -- workflow will run for every trigger match.
          </p>
        )}

        {conditions.map((condition, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Field name"
              value={condition.field}
              onChange={(e) => updateCondition(index, { field: e.target.value })}
            />
            <select
              className="input w-40"
              value={condition.operator}
              onChange={(e) =>
                updateCondition(index, {
                  operator: e.target.value as ConditionOperator,
                })
              }
            >
              {conditionOperators.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            {condition.operator !== 'is_empty' &&
              condition.operator !== 'is_not_empty' && (
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="Value"
                  value={condition.value}
                  onChange={(e) =>
                    updateCondition(index, { value: e.target.value })
                  }
                />
              )}
            <button
              type="button"
              onClick={() => removeCondition(index)}
              className="p-1.5 text-slate-400 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            Actions{' '}
            <span className="font-normal text-slate-400">({actions.length})</span>
          </h3>
          <button type="button" onClick={addAction} className="btn-secondary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Action
          </button>
        </div>

        {actions.length === 0 && (
          <p className="text-sm text-slate-400">
            Add at least one action for this workflow to perform.
          </p>
        )}

        {actions.map((action, index) => (
          <div
            key={action.id}
            className="flex items-start gap-2 rounded-lg border border-surface-200 bg-surface-50 p-3"
          >
            <div className="flex flex-col gap-1 pt-1">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => moveAction(index, 'up')}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <GripVertical className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-700">
                  {index + 1}
                </span>
                <select
                  className="input flex-1"
                  value={action.type}
                  onChange={(e) =>
                    updateAction(index, {
                      type: e.target.value as ActionType,
                    })
                  }
                >
                  {Object.entries(actionTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => moveAction(index, 'up')}
                  disabled={index === 0}
                  className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                  title="Move up"
                >
                  <ChevronRight className="h-4 w-4 -rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={() => moveAction(index, 'down')}
                  disabled={index === actions.length - 1}
                  className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                  title="Move down"
                >
                  <ChevronRight className="h-4 w-4 rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={() => removeAction(index)}
                  className="p-1 text-slate-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSaving || !name.trim()}>
          {isSaving ? 'Saving...' : initial ? 'Update Workflow' : 'Create Workflow'}
        </button>
      </div>
    </form>
  );
}

// --- Workflow History ---

interface WorkflowHistoryPanelProps {
  workflowId: string;
  workflowName: string;
  onBack: () => void;
}

function WorkflowHistoryPanel({ workflowId, workflowName, onBack }: WorkflowHistoryPanelProps) {
  const { data, isLoading } = useWorkflowHistory(workflowId);

  const statusConfig: Record<string, { icon: typeof CheckCircle2; color: string }> = {
    success: { icon: CheckCircle2, color: 'text-green-500' },
    failure: { icon: XCircle, color: 'text-red-500' },
    skipped: { icon: SkipForward, color: 'text-slate-400' },
  };

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to workflows
      </button>

      <h2 className="text-lg font-semibold text-slate-900 mb-1">Execution History</h2>
      <p className="text-sm text-slate-500 mb-4">{workflowName}</p>

      {isLoading ? (
        <div className="card divide-y divide-surface-100">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <div className="h-5 w-5 animate-pulse rounded-full bg-surface-200" />
              <div className="flex-1">
                <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                <div className="mt-1 h-3 w-32 animate-pulse rounded bg-surface-100" />
              </div>
            </div>
          ))}
        </div>
      ) : !data?.history.length ? (
        <EmptyState
          icon={History}
          title="No execution history"
          description="This workflow has not been triggered yet."
        />
      ) : (
        <div className="card divide-y divide-surface-100">
          {data.history.map((entry) => {
            const cfg = statusConfig[entry.status] ?? statusConfig.skipped;
            const Icon = cfg.icon;
            return (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                <Icon className={cn('h-5 w-5 shrink-0', cfg.color)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 capitalize">
                      {entry.status}
                    </span>
                    <span className="text-xs text-slate-400">
                      {entry.actionsExecuted} action{entry.actionsExecuted !== 1 ? 's' : ''} executed
                    </span>
                  </div>
                  {entry.error && (
                    <p className="mt-0.5 text-xs text-red-500 truncate">{entry.error}</p>
                  )}
                  <p className="text-xs text-slate-400">
                    Triggered by {entry.triggeredBy}
                    {entry.entityType && ` on ${entry.entityType}`}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-slate-400">
                  {new Date(entry.startedAt).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Main View ---

export function WorkflowsView({ onNavigate }: WorkflowsViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [searchText, setSearchText] = useState('');
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | undefined>();
  const [historyWorkflow, setHistoryWorkflow] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data, isLoading, error } = useWorkflows(
    searchText ? { search: searchText } : undefined,
  );
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const deleteWorkflow = useDeleteWorkflow();
  const toggleWorkflow = useToggleWorkflow();

  function handleEdit(workflow: Workflow) {
    setEditingWorkflow(workflow);
    setMode('form');
  }

  function handleCreate() {
    setEditingWorkflow(undefined);
    setMode('form');
  }

  function handleSave(formData: Partial<Workflow>) {
    if (editingWorkflow) {
      updateWorkflow.mutate(
        { id: editingWorkflow.id, data: formData },
        { onSuccess: () => setMode('list') },
      );
    } else {
      createWorkflow.mutate(formData, { onSuccess: () => setMode('list') });
    }
  }

  function handleShowHistory(workflow: Workflow) {
    setHistoryWorkflow({ id: workflow.id, name: workflow.name });
    setMode('history');
  }

  const workflows = data?.workflows ?? [];

  // --- History Mode ---
  if (mode === 'history' && historyWorkflow) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Workflows</h1>
            <p className="mt-0.5 text-sm text-slate-500">Execution history</p>
          </div>
        </div>
        <WorkflowHistoryPanel
          workflowId={historyWorkflow.id}
          workflowName={historyWorkflow.name}
          onBack={() => setMode('list')}
        />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingWorkflow ? 'Edit Workflow' : 'New Workflow'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingWorkflow
                ? 'Update the workflow configuration'
                : 'Configure a new automation workflow'}
            </p>
          </div>
        </div>
        <WorkflowForm
          initial={editingWorkflow}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createWorkflow.isPending || updateWorkflow.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Workflows</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} workflow${data.total !== 1 ? 's' : ''}` : 'Automate your processes'}
          </p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          New Workflow
        </button>
      </div>

      {/* Search */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <form onSubmit={(e) => e.preventDefault()} className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search workflows..."
            className="input pl-9"
          />
        </form>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load workflows. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : workflows.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            {workflows.map((workflow) => (
              <div
                key={workflow.id}
                className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50 transition-colors"
              >
                {/* Icon */}
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-lg',
                    workflow.isActive
                      ? 'bg-brand-100 text-brand-600'
                      : 'bg-surface-100 text-slate-400',
                  )}
                >
                  <Zap className="h-5 w-5" />
                </div>

                {/* Info */}
                <div
                  className="min-w-0 flex-1 cursor-pointer"
                  onClick={() => handleEdit(workflow)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">
                      {workflow.name}
                    </span>
                    <Badge
                      color={workflow.isActive ? 'green' : 'gray'}
                      size="sm"
                    >
                      {workflow.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                    <span>{triggerTypeLabels[workflow.trigger.type]}</span>
                    <span>{workflow.actions.length} action{workflow.actions.length !== 1 ? 's' : ''}</span>
                    <span>{workflow.runCount} run{workflow.runCount !== 1 ? 's' : ''}</span>
                    {workflow.lastRunAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last: {new Date(workflow.lastRunAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleShowHistory(workflow)}
                    className="p-1.5 text-slate-400 hover:text-slate-600"
                    title="View history"
                  >
                    <History className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleWorkflow.mutate(workflow.id)}
                    className={cn(
                      'p-1.5',
                      workflow.isActive
                        ? 'text-green-500 hover:text-orange-500'
                        : 'text-slate-400 hover:text-green-500',
                    )}
                    title={workflow.isActive ? 'Pause workflow' : 'Activate workflow'}
                  >
                    {workflow.isActive ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this workflow?')) {
                        deleteWorkflow.mutate(workflow.id);
                      }
                    }}
                    className="p-1.5 text-slate-400 hover:text-red-500"
                    title="Delete workflow"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Zap}
            title="No workflows"
            description="Create your first workflow to automate repetitive tasks."
            actionLabel="Create Workflow"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
